import React, { useMemo } from 'react';
import { Check, X } from 'lucide-react';

const RULES = [
    { id: 'length', label: `Minim 8 caractere`, test: (v) => v.length >= 8 },
    { id: 'upper', label: `O literă mare (A-Z)`, test: (v) => /[A-Z]/.test(v) },
    { id: 'digit', label: `O cifră (0-9)`, test: (v) => /\d/.test(v) },
    { id: 'symbol', label: `Un simbol (! @ # $ % & * ?)`, test: (v) => /[!"#$%&'()*+,\-./:;<=>?@[\]\\^_`{|}~]/.test(v) },
];

/**
 * Live password complexity checklist. Mounts under any password input and
 * shows which rules are satisfied as the user types. `value` is the current
 * password string. `show` defaults to true; pass false to keep the component
 * mounted but hidden (e.g. you only want it visible while the input is focused).
 *
 * Use `isPasswordValid(value)` to gate submit buttons.
 */
export function PasswordStrength({ value = '', show = true, compact = false }) {
    const results = useMemo(() => RULES.map(r => ({ ...r, ok: r.test(value) })), [value]);
    if (!show) return null;
    const allOk = results.every(r => r.ok);
    return (
        <div
            data-testid="password-strength"
            style={{
                marginTop: 6,
                padding: compact ? '0.5rem 0.7rem' : '0.7rem 0.9rem',
                background: allOk ? 'rgba(20,184,166,0.06)' : 'rgba(255,255,255,0.03)',
                border: `1px solid ${allOk ? 'rgba(20,184,166,0.4)' : 'var(--border, rgba(255,255,255,0.1))'}`,
                borderRadius: 10,
                fontSize: compact ? '0.78rem' : '0.82rem',
                color: 'var(--text-muted)',
                lineHeight: 1.6,
            }}
        >
            {results.map(r => (
                <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 6, color: r.ok ? 'var(--neon-teal)' : 'var(--text-muted)' }}>
                    {r.ok
                        ? <Check size={14} style={{ flexShrink: 0 }} />
                        : <X size={14} style={{ flexShrink: 0, opacity: 0.5 }} />}
                    <span style={{ textDecoration: r.ok ? 'line-through' : 'none', opacity: r.ok ? 0.7 : 1 }}>{r.label}</span>
                </div>
            ))}
        </div>
    );
}

/** Returns true if the password satisfies ALL rules. Use to disable submit buttons. */
export function isPasswordValid(value) {
    return RULES.every(r => r.test(value || ''));
}
